/**
 * Logger Utility
 * 
 * Logging service for Cuesesh application.
 * Provides environment-aware logging with production safety.
 * 
 * Features:
 * - Environment-based log levels
 * - Structured logging with metadata
 * - Performance monitoring
 * - Error tracking integration
 * - Production-safe (no console output in prod)
 * 
 * @module logger
 */

const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3,
  TRACE: 4
};

const LOG_COLORS = {
  ERROR: '\x1b[31m', // Red
  WARN: '\x1b[33m',  // Yellow
  INFO: '\x1b[36m',  // Cyan
  DEBUG: '\x1b[35m', // Magenta
  TRACE: '\x1b[90m', // Gray
  RESET: '\x1b[0m'
};

class Logger {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isTest = process.env.NODE_ENV === 'test';
    this.isProduction = process.env.NODE_ENV === 'production';
    
    // Allow overriding log level via localStorage for debugging
    this.isSilenced = typeof window !== 'undefined' && 
                      localStorage.getItem('CUESESH_SILENT_LOGS') === 'true';
    
    // Set log level based on environment
    this.logLevel = this.getLogLevel();
    
    // Queue for batching logs in production
    this.logQueue = [];
    this.batchSize = 50;
    this.batchInterval = 5000; // 5 seconds
    
    // Start batch processing in production
    if (this.isProduction) {
      this.startBatchProcessing();
    }
  }

  getLogLevel() {
    if (this.isProduction) return LOG_LEVELS.ERROR;
    if (this.isTest) return LOG_LEVELS.WARN;
    // In development, only show INFO and above to reduce console noise
    // This eliminates 3000+ debug/trace messages while keeping important logs
    return LOG_LEVELS.INFO; // Development - reduced from DEBUG to INFO
  }

  /**
   * Format log message with metadata
   */
  formatMessage(level, message, context = {}) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level,
      message,
      context,
      environment: process.env.NODE_ENV,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
      url: typeof window !== 'undefined' ? window.location.href : 'N/A'
    };

    // Add stack trace for errors - safely handle null context
    if (level === 'ERROR' && context && context.error) {
      logEntry.stack = context.error.stack || context.error.toString();
    }

    return logEntry;
  }

  /**
   * Send logs to monitoring service in production
   */
  async sendToMonitoring(logs) {
    // In production, this would send to your monitoring service
    // For now, we'll just store in localStorage for debugging
    if (typeof window !== 'undefined') {
      try {
        const existingLogs = JSON.parse(localStorage.getItem('cuesesh_logs') || '[]');
        const updatedLogs = [...existingLogs, ...logs].slice(-1000); // Keep last 1000 logs
        localStorage.setItem('cuesesh_logs', JSON.stringify(updatedLogs));
      } catch (error) {
        // Silently fail if localStorage is full or unavailable
      }
    }
  }

  /**
   * Start batch processing for production logs
   */
  startBatchProcessing() {
    setInterval(() => {
      if (this.logQueue.length > 0) {
        const logsToSend = this.logQueue.splice(0, this.batchSize);
        this.sendToMonitoring(logsToSend);
      }
    }, this.batchInterval);
  }

  /**
   * Core logging method
   */
  log(level, message, context = {}) {
    const levelValue = LOG_LEVELS[level];
    
    // Check if we should log based on current log level
    if (levelValue > this.logLevel) return;
    
    // Check if logging is completely silenced
    if (this.isSilenced && level !== 'ERROR') return;

    const logEntry = this.formatMessage(level, message, context);

    // In production, queue logs for batch processing
    if (this.isProduction) {
      this.logQueue.push(logEntry);
      
      // Force send if error
      if (level === 'ERROR') {
        this.sendToMonitoring([logEntry]);
      }
      return;
    }

    // In development/test, output to console with colors
    if (this.isDevelopment || this.isTest) {
      const color = LOG_COLORS[level] || LOG_COLORS.RESET;
      const prefix = `${color}[${level}]${LOG_COLORS.RESET}`;
      
      // Use appropriate console method
      switch (level) {
        case 'ERROR':
          console.error(prefix, message, context);
          break;
        case 'WARN':
          console.warn(prefix, message, context);
          break;
        case 'INFO':
          console.info(prefix, message, context);
          break;
        default:
          console.log(prefix, message, context);
      }
    }
  }

  /**
   * Public logging methods
   */
  error(message, context = {}) {
    this.log('ERROR', message, context);
  }

  warn(message, context = {}) {
    this.log('WARN', message, context);
  }

  info(message, context = {}) {
    this.log('INFO', message, context);
  }

  debug(message, context = {}) {
    this.log('DEBUG', message, context);
  }

  trace(message, context = {}) {
    this.log('TRACE', message, context);
  }

  /**
   * Performance logging
   */
  time(label) {
    if (this.isDevelopment && !this.isSilenced) {
      console.time(label);
    }
  }

  timeEnd(label) {
    if (this.isDevelopment && !this.isSilenced) {
      console.timeEnd(label);
    }
  }

  /**
   * Group logging for better organization
   */
  group(label) {
    if (this.isDevelopment && !this.isSilenced) {
      console.group(label);
    }
  }

  groupEnd() {
    if (this.isDevelopment && !this.isSilenced) {
      console.groupEnd();
    }
  }

  /**
   * Table logging for structured data
   */
  table(data) {
    if (this.isDevelopment && !this.isSilenced) {
      console.table(data);
    }
  }

  /**
   * Get stored logs (for debugging)
   */
  getLogs() {
    if (typeof window !== 'undefined') {
      try {
        return JSON.parse(localStorage.getItem('cuesesh_logs') || '[]');
      } catch {
        return [];
      }
    }
    return [];
  }

  /**
   * Clear stored logs
   */
  clearLogs() {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('cuesesh_logs');
    }
    this.logQueue = [];
  }
}

// Export singleton instance
const logger = new Logger();

// Export for use in app
export default logger;

// Also export as named exports for convenience
export const { error, warn, info, debug, trace, time, timeEnd, group, groupEnd, table } = logger;